import * as React from 'react'
import * as Redux from 'react-redux'
import styled, { css } from 'styled-components'

import { t, tc } from '@owl-nest/localize'
import { BREAKPOINTS, COLORS, illustrations, Link, styles as S } from '@ulule/owl-kit-components/next'
import * as model from '@owl-nest/models'
import { CATEGORY, EVENT_TYPE, useTracking } from '@owl-nest/shadow'

import * as modalAction from '../../../../../actions/modal'
import { DisplayMode } from '../../../../context'
import * as projectModel from '../../../../models/project'
import { RootState } from '../../../../../reducers'
import * as URLs from '../../../../utils/URLs'

import { TRACKING_ACTION, TRACKING_SOURCE } from '../../utils/trackerEnums'
import { EDIT_MODE_LAPTOP_BREAKPOINT } from '../../constants'
import { FooterContent } from '../../utils/Data'

export function NewToUlule(): React.ReactElement<unknown> {
  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)
  const dispatch = Redux.useDispatch()
  const isEditMode = React.useContext(DisplayMode) === 'edit'
  const tracking = useTracking()
  const projectIsDonationBased = model.project.isDonationBased(project)

  const columns = projectIsDonationBased ? FooterContent['donationBased'] : FooterContent['rewardBased']

  return (
    <Background $isEditMode={isEditMode}>
      <ContentWrapper $isEditMode={isEditMode}>
        <StyledTitle as="h2">
          {projectIsDonationBased ? t('Fundraising on Ulule') : t('First time on Ulule?')}
        </StyledTitle>

        <Columns $isEditMode={isEditMode}>
          {columns.map((column, index) => {
            return (
              <Column key={index} $isEditMode={isEditMode}>
                <ColumnIconWrapper>{column.icon}</ColumnIconWrapper>
                <ColumnTitle as="h3">{column.title}</ColumnTitle>
                <ColumnContent>{column.content}</ColumnContent>
              </Column>
            )
          })}
        </Columns>
        {model.project.hasCheckout(project) && (
          <CTAWrapper $isEditMode={isEditMode}>
            <CTA
              $isEditMode={isEditMode}
              onClick={() => {
                if (project.is_online && !model.project.isFinished<projectModel.ExtraFields>(project)) {
                  if (!model.project.isOrdersEnabled<projectModel.ExtraFields>(project)) {
                    dispatch(modalAction.toggleModalPaymentDisturbance(true))
                  } else {
                    tracking.track({
                      event: EVENT_TYPE.GENERIC,
                      eventCategory: CATEGORY.PROJECT_PAGE,
                      eventAction: TRACKING_ACTION.ACCESS_CHECKOUT,
                      eventLabel: TRACKING_SOURCE.FIRST_TIME_FOOTER,
                    })
                    document.location.href = URLs.getCheckoutURL(project.id)
                  }
                } else if (project.post_campaign_link) {
                  dispatch(modalAction.toggleModalPostCampaign(true))
                }
              }}
              size="medium"
            >
              {projectIsDonationBased ? t('Donate') : t('Back this project')}
            </CTA>
          </CTAWrapper>
        )}

        <LinkWrapper>
          <S.copy.S.regular>
            {tc('All your questions are answered in [support: our help center].', {
              support: <Link href="https://support.ulule.com/" tinted />,
            })}
          </S.copy.S.regular>
        </LinkWrapper>
      </ContentWrapper>
    </Background>
  )
}

const Background = styled.div<{ $isEditMode: boolean }>`
  background-color: ${COLORS.PRIMARY_SAND};
  padding: 37px 10px;

  @media screen and (min-width: 768px) {
    padding: 24px 32px;
  }

  @media screen and ${({ $isEditMode }) => ($isEditMode ? EDIT_MODE_LAPTOP_BREAKPOINT : BREAKPOINTS.LAPTOP_L)} {
    padding: 68px 0px;
  }
`

const Column = styled.div<{ $isEditMode: boolean }>`
  margin-bottom: 38px;

  @media screen and (min-width: 768px) {
    width: 320px;
  }
`

const ColumnContent = styled(S.copy.M.light)``

const ColumnTitle = styled(S.heading.XS)`
  margin: 15px 0;
`

const Columns = styled.div<{ $isEditMode: boolean }>`
  ${illustrations.Illustration} {
    height: 75px;
    width: auto;
  }

  @media screen and (min-width: 768px) {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-top: 55px;

    ${illustrations.Illustration} {
      height: inherit;
      margin-left: -10px;
    }
  }
`

const ColumnIconWrapper = styled.div`
  margin: 0px;
  margin-bottom: 6px;
  width: 64px;
`

const ContentWrapper = styled.div<{ $isEditMode: boolean }>`
  @media screen and (min-width: 768px) {
    margin: 0 auto;
    max-width: 1200px;
    width: 100%;
  }
`

const CTAWrapper = styled.div<{ $isEditMode: boolean }>`
  display: none;

  @media screen and (min-width: 768px) {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
`

const CTA = styled(S.button.CallToAction)<{ $isEditMode: boolean }>`
  margin-bottom: 20px;

  @media screen and (min-width: 768px) {
    margin: 0 auto;
    margin-top: 34px;
    margin-bottom: 14px;
    width: 260px;
  }
`

const StyledTitle = styled(S.heading.XL)`
  margin-bottom: 26px;
  text-align: center;
`

const LinkWrapper = styled.div`
  text-align: center;
  margin: 0 auto;
`
